/**
 * @lodex-pro/web-dispatch — App component (auth gate + routed shell).
 *
 * Phase 4 / Plan 04-10 — auth gate skeleton (loading → <p>; !isAuthenticated → <SignInPage/>).
 * Phase 9 / Plan 09-04 — replaces the placeholder authenticated branch with the real
 * routed AppShell + React Router BrowserRouter, per PATTERNS.md App.tsx section.
 *
 * Auth gate remains the SOLE React-level protection — the API enforces Cognito JWT +
 * role-guard + Postgres RLS (CLAUDE.md three-layer security model). This component
 * does NOT make trust decisions; it is a UX convenience.
 *
 * Route structure:
 *   /            → OperationsPage (default — job queue + map + driver stack)
 *   /geofences   → GeofencesPage (Plan 09-06 body; stub here)
 *   /sync        → SyncPage (deferred; placeholder)
 *   /settings/:section → SettingsPage (left section list; /setup-config redirects here)
 *
 * Company name and logo: useTenantBrand() (web-shared) feeds the AppShell sidebar
 * header — quick 261013-srg PR 0. The server enforces tenant scoping via RLS; the
 * header is informational.
 *
 * Spec: master spec §7; CLAUDE.md branding + three-layer security model;
 *       PATTERNS.md App.tsx section; UI-SPEC Surface Inventory §1.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  Map,
  Network,
  Settings,
  Layers,
  Truck,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Hourglass,
  LayoutDashboard,
  Tablet,
} from 'lucide-react';
import {
  AppShell,
  TOASTER_OFFSET,
  useAuth,
  useActiveTenant,
  useApiOpts,
  useTenantBrand,
} from '@lodex-pro/web-shared';
import type { NavSection } from '@lodex-pro/web-shared';

import { SignInPage } from './auth/SignInPage';
import { TenantSelect } from './auth/TenantSelect';
import { MapProvider } from './features/map/MapProvider';
import { OperationsPage } from './routes/OperationsPage';
import { GeofencesPage } from './routes/GeofencesPage';
// Phase 26 / Task 26.5 (DEC-147 #10) — Management console retired. Its
// Operators / Sites / Tanks tabs are gone; Sites & Operators is now the sole
// hauler-catalog console.
import { SitesOperatorsPage } from './routes/SitesOperatorsPage';
import { SetupWizard } from './routes/SetupWizard';
import { CatalogChangesPage } from './routes/CatalogChangesPage';
import { PlatformChangesPage } from './routes/PlatformChangesPage';
import { CatalogChangesBanner } from './features/catalog-changes/CatalogChangesBanner';
import { FleetPage } from './routes/FleetPage';
import { SyncPage } from './routes/SyncPage';
import { settingsRoutes } from './features/settings/settings-routes';
// DEC-315 option A — read-only stuck-jobs list.
import { StuckJobsPage } from './routes/StuckJobsPage';
// OPEN-036 / DEC-335 — shared tablets + "Sign out now" (§7 remote clear).
import { TabletsPage } from './routes/TabletsPage';
// Phase 19 / Task 19.2 (DEC-325) — Operations dashboard.
import { DashboardPage } from './routes/DashboardPage';
// Phase 19 / Task 19.3 (DEC-325) — Management dashboard.
import { ManagementDashboardPage } from './routes/ManagementDashboardPage';
// Phase 13 / Task 13.3 (DEC-351) — dispatcher Tanks tab; the route redirects to /dashboard
// when the company has the tab switched off (DEC-351 addendum, 0192).
import { TanksDashboardRoute } from './routes/TanksDashboardPage';
import { NotificationBell } from './features/notifications/NotificationBell';
import { useUserProfile } from './lib/use-user-timezone';
import { confirmNavigation } from './lib/navigation-guard';
import { useWizardStatus } from './features/setup-wizard/hooks';
// MANAGE_ROLES — roles that may manage reference data (operators/sites/tanks), the Wave 3 nav
// gate; WIZARD_GATE_ROLES — who gets the new-hauler wizard gate. Both apply the DEC-313 union.
import {
  MANAGE_ROLES,
  OPS_DASHBOARD_ROLES,
  TABLET_ROLES,
  WIZARD_GATE_ROLES,
} from './lib/role-gates';

/** Sites & Operators (hauler catalog junctions) — Phase 24 / Task 24.3 (DEC-094).
 *  Same MANAGE_ROLES nav gate as Management; the read tabs are still server-gated
 *  to VIEW_HAULER_CATALOG (DISPATCHER+) for anyone who reaches the route. */
const SITES_OPERATORS_NAV: NavSection = {
  id: 'sites-operators',
  label: 'Sites & Operators',
  icon: <MapPin size={16} aria-hidden="true" />,
};

/** Fleet console nav — same MANAGE_ROLES gate as Management (Quick 260616-flt / DEC-067). */
const FLEET_NAV: NavSection = {
  id: 'fleet',
  label: 'Fleet',
  icon: <Truck size={16} aria-hidden="true" />,
};

/** Operator Changes nav — Phase 24 / Task 24.9 (DEC-100). MANAGE_ROLES gate
 *  (hauler review actions are DISPATCH_MANAGER+ via MANAGE_HAULER_CATALOG). */
const CATALOG_CHANGES_NAV: NavSection = {
  id: 'catalog-changes',
  label: 'Operator Changes',
  icon: <RefreshCw size={16} aria-hidden="true" />,
};

/** Platform change-queue nav — Phase 24 / Task 24.9 (DEC-100). PLATFORM_ADMIN only. */
const PLATFORM_CHANGES_NAV: NavSection = {
  id: 'platform-changes',
  label: 'Platform Changes',
  icon: <ShieldCheck size={16} aria-hidden="true" />,
};

/** Tablets nav — OPEN-036 / DEC-335. TABLET_ROLES gate (the CLEAR_SHARED_DEVICE holders). */
const TABLETS_NAV: NavSection = {
  id: 'tablets',
  label: 'Tablets',
  icon: <Tablet size={16} aria-hidden="true" />,
};

/** Operations dashboard nav — Phase 19 / Task 19.2 (DEC-325 D-2). OPS_DASHBOARD_ROLES gate. */
const DASHBOARD_NAV: NavSection = {
  id: 'dashboard',
  label: 'Dashboard',
  icon: <LayoutDashboard size={16} aria-hidden="true" />,
};

/** Navigation sections per UI-SPEC Surface Inventory §1. Management is appended
 *  for MANAGE_REFERENCE_DATA roles only (Wave 3). */
const DISPATCH_NAV: readonly NavSection[] = [
  { id: 'operations', label: 'Operations', icon: <Layers size={16} aria-hidden="true" /> },
  // DEC-315 — every dispatch role sees it; the API gates the data (VIEW_RUN_TICKETS, not DRIVER).
  { id: 'stuck-jobs', label: 'Stuck jobs', icon: <Hourglass size={16} aria-hidden="true" /> },
  { id: 'geofences', label: 'Geofences', icon: <Map size={16} aria-hidden="true" /> },
  { id: 'sync', label: 'Sync', icon: <Network size={16} aria-hidden="true" /> },
  { id: 'settings', label: 'Settings', icon: <Settings size={16} aria-hidden="true" /> },
];

/** Maps route pathname to nav section id. */
function pathnameToNavId(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/stuck-jobs')) return 'stuck-jobs';
  if (pathname.startsWith('/tablets')) return 'tablets';
  if (pathname.startsWith('/geofences')) return 'geofences';
  if (pathname.startsWith('/sites-operators')) return 'sites-operators';
  if (pathname.startsWith('/fleet')) return 'fleet';
  if (pathname.startsWith('/platform/catalog/changes')) return 'platform-changes';
  if (pathname.startsWith('/catalog-changes')) return 'catalog-changes';
  if (pathname.startsWith('/sync')) return 'sync';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'operations';
}

/** Maps nav section id to route pathname. */
function navIdToPathname(id: string): string {
  switch (id) {
    case 'dashboard':
      return '/dashboard';
    case 'stuck-jobs':
      return '/stuck-jobs';
    case 'tablets':
      return '/tablets';
    case 'geofences':
      return '/geofences';
    case 'sites-operators':
      return '/sites-operators';
    case 'fleet':
      return '/fleet';
    case 'catalog-changes':
      return '/catalog-changes';
    case 'platform-changes':
      return '/platform/catalog/changes';
    case 'sync':
      return '/sync';
    case 'settings':
      return '/settings';
    default:
      return '/';
  }
}

/** Maps route pathname to page title for the AppShell top bar. */
function pathnameToPageTitle(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/stuck-jobs')) return 'Stuck jobs';
  if (pathname.startsWith('/tablets')) return 'Tablets';
  if (pathname.startsWith('/geofences')) return 'Geofences';
  if (pathname.startsWith('/sites-operators')) return 'Sites & Operators';
  if (pathname.startsWith('/fleet')) return 'Fleet';
  if (pathname.startsWith('/platform/catalog/changes')) return 'Platform Changes';
  if (pathname.startsWith('/catalog-changes')) return 'Operator Changes';
  if (pathname.startsWith('/sync')) return 'Sync';
  if (pathname.startsWith('/settings')) return 'Settings';
  return 'Operations';
}

/** Inner routed shell — must be inside BrowserRouter to use useLocation/useNavigate. */
function DispatchShell(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOutAll } = useAuth();

  // Wave 3 — show the Management nav only for MANAGE_REFERENCE_DATA roles.
  const { data: profileData } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const canManage = MANAGE_ROLES.has(role);
  const isPlatformAdmin = role === 'PLATFORM_ADMIN';
  const canSeeDashboard = OPS_DASHBOARD_ROLES.has(role);
  const canSeeTablets = TABLET_ROLES.has(role);
  const nav = React.useMemo((): readonly NavSection[] => {
    const base = canSeeDashboard
      ? [DISPATCH_NAV[0], DASHBOARD_NAV, ...DISPATCH_NAV.slice(1)].filter(
          (s): s is NavSection => s !== undefined,
        )
      : DISPATCH_NAV;
    const withTablets = canSeeTablets ? [...base, TABLETS_NAV] : base;
    return canManage
      ? [
          ...withTablets,
          SITES_OPERATORS_NAV,
          FLEET_NAV,
          CATALOG_CHANGES_NAV,
          ...(isPlatformAdmin ? [PLATFORM_CHANGES_NAV] : []),
        ]
      : withTablets;
  }, [canManage, canSeeDashboard, canSeeTablets, isPlatformAdmin]);

  const activeNavId = pathnameToNavId(location.pathname);
  const pageTitle = pathnameToPageTitle(location.pathname);

  const handleNavigate = React.useCallback(
    (id: string): void => {
      // A screen mid-task (an import running) can ask to be confirmed first.
      if (!confirmNavigation()) return;
      void navigate(navIdToPathname(id));
    },
    [navigate],
  );

  // Identity from the SERVER profile (source of truth) — not the Amplify client
  // object, whose signInDetails.loginId is absent on a restored session (page
  // reload). Amplify values are a last-resort fallback; 'Unknown user' only if
  // nothing resolves (never a fake-looking role name like the old 'Dispatcher').
  const userName =
    profileData?.profile.fullName ??
    profileData?.profile.email ??
    user?.signInDetails?.loginId ??
    user?.username ??
    'Unknown user';

  // Quick 261013-srg PR 0 — the company's real name and logo for the sidebar header
  // (was the 'Hauler Tenant' placeholder). Initials show until a logo is uploaded.
  const { tenantName, logo: tenantLogo } = useTenantBrand();

  return (
    <AppShell
      nav={nav}
      activeNavId={activeNavId}
      onNavigate={handleNavigate}
      pageTitle={pageTitle}
      tenantName={tenantName}
      tenantLogo={tenantLogo}
      userName={userName}
      role={role}
      onSignOut={(): void => {
        if (!confirmNavigation()) return;
        void signOutAll();
      }}
      headerRight={<NotificationBell />}
    >
      {/* Task 24.9 — amber banner for DISPATCH_MANAGER+ when operator changes await review. */}
      <CatalogChangesBanner
        enabled={canManage}
        onReview={(): void => handleNavigate('catalog-changes')}
      />
      <Routes>
        <Route path="/" element={<OperationsPage />} />
        <Route path="/stuck-jobs" element={<StuckJobsPage />} />
        <Route path="/tablets" element={<TabletsPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/tanks" element={<TanksDashboardRoute />} />
        <Route path="/dashboard/management" element={<ManagementDashboardPage />} />
        <Route path="/geofences" element={<GeofencesPage />} />
        <Route path="/sites-operators" element={<SitesOperatorsPage />} />
        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/catalog-changes" element={<CatalogChangesPage />} />
        <Route path="/platform/catalog/changes" element={<PlatformChangesPage />} />
        <Route path="/sync" element={<SyncPage />} />
        {/* Quick 261013-srg PR 1 — /settings/:section, and /setup-config redirecting to it. */}
        {settingsRoutes()}
      </Routes>
    </AppShell>
  );
}

export function App(): React.ReactElement {
  const { isAuthenticated, loading } = useAuth();
  const apiOpts = useApiOpts();

  // Tenant bootstrap (Quick 260614-pli) — runs ONLY once authenticated. A user
  // whose active_tenant_id is NULL (default at provisioning) would otherwise
  // reach the shell and 403 on every tenant-scoped call ("Could not load jobs").
  // Sole-membership users are auto-selected here; multi-membership users pick.
  const { state: tenantState, selectTenant } = useActiveTenant(
    apiOpts,
    isAuthenticated && !loading,
  );

  // Phase 24 / Task 24.8 (DEC-099) — new-hauler setup-wizard gate. Hooks run
  // unconditionally (rules-of-hooks); the wizard status only fetches once the
  // tenant is ready AND the role is gated (TENANT_ADMIN / DISPATCH_MANAGER).
  const { data: gateProfile } = useUserProfile();
  const gateRole = gateProfile?.profile.role ?? '';
  const tenantReady = isAuthenticated && !loading && tenantState.kind === 'ready';
  const gatedRole = WIZARD_GATE_ROLES.has(gateRole);
  const { data: wizardStatus } = useWizardStatus(tenantReady && gatedRole);

  if (loading) {
    return <p role="status">Loading…</p>;
  }
  if (!isAuthenticated) {
    return <SignInPage />;
  }

  // Authenticated — resolve the active tenant before mounting the shell.
  if (tenantState.kind === 'checking') {
    return <p role="status">Loading workspace…</p>;
  }
  if (tenantState.kind === 'select') {
    return (
      <TenantSelect
        memberships={tenantState.memberships}
        onSelect={(tenantId): void => {
          void selectTenant(tenantId);
        }}
      />
    );
  }
  if (tenantState.kind === 'none') {
    return (
      <main className="auth-shell" aria-label="No workspace">
        <div className="auth-card">
          <h1 className="auth-brand">lodeX Pro Dispatch</h1>
          <p className="auth-subtitle">
            Your account isn&apos;t assigned to a workspace yet. Contact your administrator.
          </p>
        </div>
      </main>
    );
  }
  // Quick 260816-vr9 — a revoked member is NOT a new user. Both arrive here with
  // an empty membership list, so the 'none' copy above used to cover both and
  // told someone who had just been deactivated that they were never set up —
  // advice they cannot act on. This branch is the whole user-visible point of
  // the ACCESS_REVOKED work; without it the API-side fix (PR #225) is inert.
  // "workspace" rather than the catalog's "tenant": these apps never say tenant.
  if (tenantState.kind === 'revoked') {
    return (
      <main className="auth-shell" aria-label="Access removed">
        <div className="auth-card">
          <h1 className="auth-brand">lodeX Pro Dispatch</h1>
          <p className="auth-subtitle">
            Your access to this workspace has been removed. Contact your administrator.
          </p>
        </div>
      </main>
    );
  }
  // DEC-349 (OPEN-134) — the company is suspended and the user has no other one.
  // Before this branch the sole-membership auto-switch put them into an empty app
  // as "Unknown user". Copy matches the API's TENANT_SUSPENDED message.
  if (tenantState.kind === 'suspended') {
    return (
      <main className="auth-shell" aria-label="Company suspended">
        <div className="auth-card">
          <h1 className="auth-brand">lodeX Pro Dispatch</h1>
          <p className="auth-subtitle">
            This company&apos;s account is suspended. Contact your administrator.
          </p>
        </div>
      </main>
    );
  }
  if (tenantState.kind === 'error') {
    return (
      <main className="auth-shell" aria-label="Workspace error">
        <div className="auth-card">
          <h1 className="auth-brand">lodeX Pro Dispatch</h1>
          <p className="auth-subtitle">Couldn&apos;t load your workspace. Refresh to try again.</p>
        </div>
      </main>
    );
  }

  // tenantState.kind === 'ready'
  // Phase 24 / Task 24.8 (DEC-099) — block a new hauler's TENANT_ADMIN/DISPATCH_MANAGER
  // into the setup wizard until it is complete OR explicitly skipped (banner dismissed).
  // Rendered full-screen, outside the routed shell; carries its own Toaster.
  if (
    gatedRole &&
    wizardStatus !== undefined &&
    !wizardStatus.allComplete &&
    !wizardStatus.wizardBannerDismissed
  ) {
    // onExit is a no-op: the wizard's PATCH mutation updates the WIZARD_STATUS_KEY
    // cache on success, so this App's useWizardStatus re-renders and the gate
    // releases automatically.
    return (
      <>
        <Toaster position="top-right" theme="dark" offset={TOASTER_OFFSET} />
        <SetupWizard onExit={(): void => undefined} />
      </>
    );
  }

  return (
    <BrowserRouter>
      {/* Sonner toast container — top-right, mounted once here (UI-SPEC §7) */}
      <Toaster position="top-right" theme="dark" offset={TOASTER_OFFSET} />
      {/* Wave 2 (#1) — one shared Mapbox instance for the whole session; lives
          above Routes so it persists across Operations ↔ Geofences navigation. */}
      <MapProvider>
        <DispatchShell />
      </MapProvider>
    </BrowserRouter>
  );
}
